import React, { useState, useCallback, useEffect, useRef } from 'react';
import { TapCardGesture, PinchGesture, TapGesture, PulseGesture, SlideUpGesture } from './helpGestures';
import './HelpOverlay.css';

// Legacy key (browser-wide). Kept for the cleanup pass below so existing users
// migrate to per-user keys without re-seeing the overlay.
const LEGACY_KEY = 'dekko-help-seen';
const KEY_PREFIX = 'dekko-help-seen:';
const storageKey = (uid: string | undefined | null) =>
  `${KEY_PREFIX}${uid || 'anonymous'}`;

interface HelpStep {
  gesture: React.ReactNode;
  title: string;
  desc: string;
}

// Copy must match what GlassCardStack actually does: it advances on a card
// TAP (GlassCardStack.tsx handleCardClick) and has no swipe handler at all.
// Guarded by tests/helpOverlay.test.ts.
export const STEPS: HelpStep[] = [
  {
    gesture: <TapCardGesture />,
    title: 'Tap for the next task',
    desc: 'Tap a card to move to your next task',
  },
  {
    gesture: <PinchGesture />,
    title: 'Pinch to see all',
    desc: 'Pinch with two fingers to switch to a grid view of all tasks',
  },
  {
    gesture: <TapGesture />,
    title: 'Tap to begin',
    desc: 'Tap the button on any card to start or resume a task',
  },
  {
    gesture: <PulseGesture />,
    title: 'Track your progress',
    desc: 'The dots at the top show how far along you are',
  },
  {
    gesture: <SlideUpGesture />,
    title: 'View submissions',
    desc: 'Tap the bar at the bottom to see what you\'ve completed',
  },
];

interface HelpOverlayProps {
  onClose: () => void;
  userId?: string | null;
}

export const HelpOverlay: React.FC<HelpOverlayProps> = ({ onClose, userId }) => {
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [stepExiting, setStepExiting] = useState(false);
  const nextStepRef = useRef<number | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const dismiss = useCallback(() => {
    localStorage.setItem(storageKey(userId), '1');
    setExiting(true);
    setTimeout(onClose, 260);
  }, [onClose, userId]);

  const advance = useCallback(() => {
    if (stepExiting) return;
    if (step >= STEPS.length - 1) {
      dismiss();
      return;
    }
    nextStepRef.current = step + 1;
    setStepExiting(true);
    setTimeout(() => {
      setStep(nextStepRef.current!);
      setStepExiting(false);
    }, 210);
  }, [step, stepExiting, dismiss]);

  // Keyboard: Escape to close, right arrow / space to advance
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
      if (e.key === 'ArrowRight' || e.key === ' ') advance();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dismiss, advance]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    // Left-only swipe, 40px threshold, horizontal must beat vertical.
    if (dx < -40 && Math.abs(dx) > Math.abs(dy)) advance();
  };

  const current = STEPS[step];

  return (
    <div
      className={`ho-scrim ${exiting ? 'ho-scrim--exit' : ''}`}
      onClick={advance}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button
        className="ho-skip"
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
      >
        Skip
      </button>

      <div
        key={step}
        className={`ho-step ${stepExiting ? 'ho-step--exit' : ''}`}
      >
        <div className="ho-gesture-wrap">
          {current.gesture}
        </div>
        <div className="ho-title">{current.title}</div>
        <div className="ho-desc">{current.desc}</div>
      </div>

      <div className="ho-footer">
        <div className="ho-dots">
          {STEPS.map((_, i) => (
            <div key={i} className={`ho-dot ${i === step ? 'ho-dot--active' : ''}`} />
          ))}
        </div>
        <div className="ho-tap-hint">Tap to continue</div>
      </div>
    </div>
  );
};

/**
 * Returns true if the given user hasn't seen the help overlay yet.
 * Migrates legacy browser-wide flag into the per-user key on first check so
 * existing users who already dismissed it don't see it again after this change.
 */
export function shouldShowHelp(userId?: string | null): boolean {
  const key = storageKey(userId);
  if (!localStorage.getItem(key) && userId && localStorage.getItem(LEGACY_KEY)) {
    localStorage.setItem(key, '1');
  }
  return !localStorage.getItem(key);
}

